import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { FormControl } from '@material-ui/core';
import { Typography } from '@material-ui/core';
import { FieldHelperOrErrors } from './FieldHelperOrErrors';
import { useStyles } from './Field.styles';
import { FormFieldRef, InputFieldProps } from './Field.props';
import { shallowCompare } from './shallowCompare';
import { useDebounce } from 'use-lodash-debounce';
import { TypedFormFieldValue } from '../Form/Form.types';

export const TextAreaField = memo(
  forwardRef<FormFieldRef<string>, InputFieldProps>((props, ref) => {
    const classes = useStyles();
    const {
      isDisabled,
      label,
      helperText,
      errorTexts,
      onChange,
      required,
      initialState,
      hideRequiredAsterisk,
      icon,
      ...rest
    } = props;

    const [text, setText] = useState((initialState as TypedFormFieldValue<string> | undefined)?.value ?? '');
    const debouncedText = useDebounce(text, 200);

    const triggerOnChange = useCallback(
      (text: string) => {
        if (onChange) {
          if (!required && text === '') {
            onChange(undefined);
          } else {
            onChange({ value: text, isValid: !required || text !== '' });
          }
        }
      },
      [required, onChange],
    );

    useImperativeHandle(
      ref,
      () => ({
        setValue: (value: TypedFormFieldValue<string> | TypedFormFieldValue<string>[] | undefined) => {
          if (!Array.isArray(value)) {
            setText(value?.value ?? '');
          }
        },
      }),
      [],
    );

    useEffect(() => {
      triggerOnChange(debouncedText);
    }, [debouncedText, triggerOnChange]);

    const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setText(event.target.value);
    }, []);

    return (
      <FormControl error={props.error} className={classes.formControl}>
        <Typography className={classes.label} component="label" htmlFor={props.name}>
          {props.label}
          {props.required && props.label && !hideRequiredAsterisk && <span aria-hidden="true">*</span>}
          {icon && icon}
        </Typography>
        <textarea
          {...(rest as any)}
          onChange={handleChange}
          value={text}
          disabled={isDisabled}
          id={props.name}
          style={{
            width: '100%',
            padding: '8px',
            borderRadius: '4px',
            border: '1px solid #ccc',
            fontFamily: 'inherit',
            fontSize: '1rem',
            minHeight: '20px',
            resize: 'vertical',
          }}
          className={props.error ? 'textarea-error' : ''}
        />
        <FieldHelperOrErrors helperText={helperText} errorTexts={errorTexts} />
      </FormControl>
    );
  }),
  (newProps, oldProps) => {
    return shallowCompare(newProps, oldProps, ['errorTexts']);
  },
);

export default TextAreaField;
